import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { useTranslation } from "../../../utils/translation";
import {
  assignUserDemo,
  listUsers,
  setUserStandard,
  selectUsers,
  selectUsersError,
  selectUsersLoading,
  selectUsersMessage,
  selectUsersUpdateError,
  selectUsersUpdateMessage,
} from "../userSlice";

const PAGE_SIZE = 10;
const TABLE_COL_COUNT = 6;

const toDatetimeLocalValue = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const UsersDemoPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const users = useSelector(selectUsers);
  const isLoading = useSelector(selectUsersLoading);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);
  const updateError = useSelector(selectUsersUpdateError);
  const updateMessage = useSelector(selectUsersUpdateMessage);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pendingUserId, setPendingUserId] = useState("");
  const [localSuccess, setLocalSuccess] = useState("");
  const [localError, setLocalError] = useState("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [demoStartAt, setDemoStartAt] = useState(toDatetimeLocalValue());
  const [demoEndAt, setDemoEndAt] = useState(toDatetimeLocalValue());

  const buildMailStatusSuffix = (result) => {
    const status = result?.mailStatus;
    if (status === "sent") return " Kullanıcıya mail gönderilmiştir.";
    if (status === "failed") return " Demo tanımlandı, ancak mail gönderilemedi.";
    if (status === "skipped") return " Demo tanımlandı, ancak e-posta adresi olmadığı için mail gönderilmedi.";
    return "";
  };

  useEffect(() => {
    dispatch(listUsers());
  }, [dispatch]);

  const getFullName = (user) => {
    if (user?.fullName) return user.fullName;
    const nameParts = [user?.name, user?.surName].filter(Boolean);
    return nameParts.length ? nameParts.join(" ") : "-";
  };

  const getEmail = (user) => user?.email || user?.userEmail || "-";

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>{match}</span>
        {after}
      </>
    );
  };

  const formatDate = (value) => {
    if (!value) return "-";
    if (typeof value === "number") return new Date(value).toLocaleString("tr-TR");
    if (typeof value?.toDate === "function") return value.toDate().toLocaleString("tr-TR");
    if (typeof value === "string") {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString("tr-TR");
      return value;
    }
    return "-";
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const safeUsers = users || [];
    if (!term) return safeUsers;
    return safeUsers.filter((user) => getFullName(user).toLowerCase().includes(term));
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const openAssignModal = (user) => {
    const now = new Date();
    const defaultStart = toDatetimeLocalValue();
    const defaultEndDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const defaultEnd = new Date(defaultEndDate.getTime() - defaultEndDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    const startValue = user?.demostartdate
      ? new Date(new Date(user.demostartdate).getTime() - new Date(user.demostartdate).getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16)
      : defaultStart;
    const endValue = user?.demoenddate
      ? new Date(new Date(user.demoenddate).getTime() - new Date(user.demoenddate).getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16)
      : defaultEnd;

    setSelectedUser(user);
    setDemoStartAt(startValue);
    setDemoEndAt(endValue);
    setShowAssignModal(true);
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedUser(null);
  };

  const assignDemoToUser = async () => {
    setLocalSuccess("");
    setLocalError("");

    const startDate = new Date(demoStartAt);
    const endDate = new Date(demoEndAt);
    if (Number.isNaN(startDate.getTime())) {
      setLocalError(t("admin_users_pages.demo_start_validation"));
      return;
    }
    if (Number.isNaN(endDate.getTime())) {
      setLocalError(t("admin_users_pages.demo_end_validation"));
      return;
    }
    if (endDate.getTime() <= startDate.getTime()) {
      setLocalError(t("admin_users_pages.demo_range_validation"));
      return;
    }

    const id = selectedUser?.id || selectedUser?.userId;
    if (!id) return;

    try {
      setPendingUserId(id);
      const result = await dispatch(
        assignUserDemo({
          id,
          demostartdate: startDate.toISOString(),
          demoenddate: endDate.toISOString(),
        })
      ).unwrap();
      setLocalSuccess(
        `${t("admin_users_pages.demo_assign_success", {
          name: getFullName(selectedUser),
          endDate: endDate.toLocaleString("tr-TR"),
        })}${buildMailStatusSuffix(result)}`
      );
      closeAssignModal();
    } catch (error) {
      setLocalError(error || t("admin_users_pages.demo_assign_failed"));
    } finally {
      setPendingUserId("");
    }
  };

  const handleSetStandard = async (user) => {
    const id = user?.id || user?.userId;
    if (!id) return;
    setLocalSuccess("");
    setLocalError("");
    try {
      setPendingUserId(id);
      await dispatch(setUserStandard({ id })).unwrap();
      setLocalSuccess(t("admin_users_pages.standard_assign_success", { name: getFullName(user) }));
    } catch (error) {
      setLocalError(error || t("admin_users_pages.standard_assign_failed"));
    } finally {
      setPendingUserId("");
    }
  };

  const renderRows = () => {
    if (isLoading && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan={TABLE_COL_COUNT}>{t("admin_common.loading")}</td>
        </tr>
      );
    }

    if (isError && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan={TABLE_COL_COUNT} className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }

    if (!pagedUsers || pagedUsers.length === 0) {
      return (
        <tr>
          <td colSpan={TABLE_COL_COUNT}>{t("admin_common.no_records")}</td>
        </tr>
      );
    }

    return pagedUsers.map((user) => {
      const id = user?.id || user?.userId;
      const isPremium = Boolean(user?.premium);
      return (
        <tr key={id}>
          <td>
            {isPremium && <i className="fa fa-star text-warning me-2" aria-hidden="true" />}
            {highlightMatch(getFullName(user), search.trim())}
          </td>
          <td>{getEmail(user)}</td>
          <td>
            <span className={`badge ${isPremium ? "badge-warning" : "badge-secondary"}`}>
              {isPremium ? t("admin_common.premium") : t("admin_common.standard")}
            </span>
          </td>
          <td>{formatDate(user?.demostartdate)}</td>
          <td>{formatDate(user?.demoenddate)}</td>
          <td>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => openAssignModal(user)}
              disabled={pendingUserId === id}
              style={{ fontSize: "11px", padding: "4px 10px", lineHeight: 1.2 }}
            >
              {pendingUserId === id ? t("admin_common.loading") : t("admin_users_pages.assign_demo")}
            </button>
            {isPremium && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm ms-2"
                onClick={() => handleSetStandard(user)}
                disabled={pendingUserId === id}
                style={{ fontSize: "11px", padding: "4px 10px", lineHeight: 1.2 }}
              >
                {t("admin_users_pages.assign_standard")}
              </button>
            )}
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{t("admin_users_pages.demo_title")}</h4>

              {localSuccess && <div className="alert alert-success">{localSuccess}</div>}
              {(localError || updateError) && (
                <div className="alert alert-danger">{localError || updateMessage || t("admin_users_pages.demo_assign_failed")}</div>
              )}

              <div className="row mb-3">
                <div className="col-md-6 mb-2 mb-md-0">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("admin_common.search_name_placeholder")}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className="col-md-6">
                  <div className="text-muted pt-2">{t("admin_users_pages.demo_modal_hint")}</div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>{t("admin_common.name")}</th>
                      <th>{t("admin_common.email")}</th>
                      <th>{t("admin_common.package")}</th>
                      <th>{t("admin_common.demo_start")}</th>
                      <th>{t("admin_common.demo_end")}</th>
                      <th>{t("admin_users_pages.assign_demo")}</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>

              <div className="d-flex align-items-center justify-content-between mt-3">
                <span className="text-muted">{t("admin_common.total_records", { count: filteredUsers.length })}</span>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("admin_common.prev")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled>
                    {currentPage} / {totalPages}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("admin_common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal className="modal fade" show={showAssignModal} onHide={closeAssignModal}>
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title fs-20">{t("admin_users_pages.assign_demo_modal_title")}</h4>
            <button type="button" className="btn-close" onClick={closeAssignModal}>
              <span></span>
            </button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_common.user")}</label>
              <input
                type="text"
                className="form-control"
                value={selectedUser ? getFullName(selectedUser) : "-"}
                disabled
              />
            </div>
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_common.demo_start")}</label>
              <input
                type="datetime-local"
                className="form-control"
                value={demoStartAt}
                onChange={(e) => setDemoStartAt(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_common.demo_end")}</label>
              <input
                type="datetime-local"
                className="form-control"
                value={demoEndAt}
                onChange={(e) => setDemoEndAt(e.target.value)}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={closeAssignModal}>
              {t("admin_common.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={assignDemoToUser}
              disabled={pendingUserId === (selectedUser?.id || selectedUser?.userId)}
            >
              {pendingUserId === (selectedUser?.id || selectedUser?.userId) ? t("admin_common.loading") : t("admin_users_pages.assign_demo")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UsersDemoPage;
